import React from "react";
import { Heading } from "@/components/ui/typography";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { SFIcon } from "@/components/ui/sf-icon";
import { sfIcons } from "@/lib/sf-icons";

const FlowDiagram: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  const steps = [
    {
      id: "wallet",
      icon: sfIcons.send,
      title: "Send Crypto",
      subtitle: "To your address", 
      color: "from-blue-500 to-cyan-400",
      description: "Any crypto, any network"
    },
    {
      id: "conversion",
      icon: sfIcons.exchange,
      title: "Auto Convert", 
      subtitle: "Best rates",
      color: "from-purple-500 to-pink-400", 
      description: "Real-time exchange"
    },
    {
      id: "bank",
      icon: sfIcons.bank,
      title: "Receive Fiat",
      subtitle: "In your bank",
      color: "from-green-500 to-emerald-400",
      description: "Direct deposit"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const stepVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0
    }
  };

  const arrowVariants = {
    hidden: { 
      opacity: 0,
      x: -20
    },
    visible: { 
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        delay: 0.8
      }
    }
  };

  const flowVariants = {
    animate: {
      x: [0, 10, 0]
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        className="text-center space-y-6 mb-16"
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-display text-display-md sm:text-display-lg bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Simple as 1-2-3
        </h2>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          The complete process happens automatically. Just send crypto and receive fiat.
        </p>
      </motion.div>

      <motion.div
        className="relative flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-4"
        variants={prefersReducedMotion ? {} : containerVariants}
        initial={prefersReducedMotion ? {} : "hidden"}
        whileInView={prefersReducedMotion ? {} : "visible"}
        viewport={{ once: true }}
      >
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Step */}
            <motion.div
              variants={prefersReducedMotion ? {} : stepVariants}
              className="relative group"
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <div className="flex flex-col items-center space-y-6 p-8 rounded-3xl bg-card border border-border/50 shadow-card hover:shadow-card-hover transition-all duration-480">
                {/* Animated Icon Container */}
                <motion.div
                  className={`relative p-6 rounded-2xl bg-gradient-to-br ${step.color} shadow-lg`}
                  whileHover={prefersReducedMotion ? {} : {
                    scale: 1.05,
                    rotate: 2,
                    transition: { duration: 0.3 }
                  }}
                >
                  {/* Glow effect */}
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${step.color} blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-480`} />
                  
                  <SFIcon icon={step.icon} size="xl" variant="white" className="relative z-10" />
                  
                  {/* Animated particles */}
                  {!prefersReducedMotion && (
                    <>
                      <motion.div
                        className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full opacity-60"
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.6, 1, 0.6]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: index * 0.2
                        }}
                      />
                      <motion.div
                        className="absolute -bottom-2 -left-2 w-2 h-2 bg-white rounded-full opacity-40"
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.4, 0.8, 0.4]
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          delay: index * 0.3
                        }}
                      />
                    </>
                  )}
                </motion.div>

                {/* Content */}
                <div className="text-center space-y-3">
                  <Heading level={3}>{step.title}</Heading>
                  <p className="text-primary font-semibold">{step.subtitle}</p>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>

                {/* Step number indicator */}
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm shadow-lg">
                  {index + 1}
                </div>
              </div>
            </motion.div>

            {/* Arrow (only between steps, not after the last one) */}
            {index < steps.length - 1 && (
              <motion.div
                variants={prefersReducedMotion ? {} : arrowVariants}
                className="flex items-center justify-center"
              >
                <motion.div
                  variants={prefersReducedMotion ? {} : flowVariants}
                  animate={prefersReducedMotion ? {} : "animate"}
                  transition={{ 
                    duration: 2,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatDelay: 1
                  }}
                  className="hidden lg:flex items-center space-x-2 text-muted-foreground"
                >
                  <div className="w-12 h-0.5 bg-gradient-to-r from-primary/50 to-primary" />
                  <SFIcon icon={sfIcons.next} size="lg" variant="primary" />
                  <div className="w-12 h-0.5 bg-gradient-to-r from-primary to-primary/50" />
                </motion.div>
                
                {/* Mobile arrow */}
                <motion.div
                  variants={prefersReducedMotion ? {} : flowVariants}
                  animate={prefersReducedMotion ? {} : "animate"}
                  transition={{ 
                    duration: 2,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatDelay: 1
                  }}
                  className="lg:hidden flex flex-col items-center space-y-2 text-muted-foreground rotate-90 lg:rotate-0"
                >
                  <div className="w-8 h-0.5 bg-gradient-to-r from-primary/50 to-primary" />
                  <SFIcon icon={sfIcons.next} size="lg" variant="primary" className="-rotate-90 lg:rotate-0" />
                  <div className="w-8 h-0.5 bg-gradient-to-r from-primary to-primary/50" />
                </motion.div>
              </motion.div>
            )}
          </React.Fragment>
        ))}

        {/* Background decorative elements */}
        {!prefersReducedMotion && (
          <>
            <motion.div
              className="absolute top-0 left-1/4 w-32 h-32 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute bottom-0 right-1/4 w-24 h-24 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full blur-2xl"
              animate={{
                scale: [1.2, 1, 1.2],
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            />
          </>
        )}
      </motion.div>
    </div>
  );
};

export { FlowDiagram };